import apiClient from '../apiClient';
import type { ApiResponse, Product, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';
import { ReactMutationKeys } from '../reactMutationKeys';

const BASE_URL = '/dashboard/products';
const ADMIN_URL = '/admin/products';

interface PaginatedResponse {
  items: Product[];
  pagination: PaginationMeta;
}

export const getProducts = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  machineId?: string,
  status?: string
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    machineId,
    status,
  });
  const { data } = await apiClient.get<ApiResponse<Product[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

export const createProduct = async (product: {
  machineId: string;
  slotNumber: number;
  name: string;
  price: number;
  discountedPrice?: number | null;
  imageUrl?: string;
}): Promise<Product> => {
  const { data } = await apiClient.post<ApiResponse<Product>>(ADMIN_URL, product);
  return data.data;
};

export const updateProduct = async (id: string, product: Partial<Product>): Promise<Product> => {
  const { data } = await apiClient.put<ApiResponse<Product>>(`${ADMIN_URL}/${id}`, product);
  return data.data;
};

export const deleteProduct = async (id: string): Promise<void> => {
  await apiClient.delete(`${ADMIN_URL}/${id}`);
};

// React Query Hooks

export const useGetProducts = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  machineId?: string,
  status?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_PRODUCTS, page, limit, search, sortBy, sortOrder, machineId, status],
    queryFn: () => getProducts(page, limit, search, sortBy, sortOrder, machineId, status),
  });

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: { machineId: string; slotNumber: number; name: string; price: number; imageUrl?: string }) =>
      createProduct(product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_MACHINES] });
    },
    mutationKey: [ReactMutationKeys.CREATE_PRODUCT],
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...product }: Partial<Product> & { id: string }) =>
      updateProduct(id, product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_PRODUCTS] });
    },
    mutationKey: [ReactMutationKeys.UPDATE_PRODUCT],
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_PRODUCTS] });
    },
    mutationKey: [ReactMutationKeys.DELETE_PRODUCT],
  });
};
